<template>
  <div class="d-flex flex-column mr-3">
    <b-table
      :fields="visibleFields"
      :items="items"
      thead-class="text-nowrap"
      :tbody-tr-class="rowClass"
    >
      <template #cell(email)="row">
        {{ row.item.email }}
        <b-badge
          v-if="isSelfUser(row.item)"
          variant="info"
          class="ml-1"
          data-cy="account-user-you"
        >
          You
        </b-badge>
      </template>

      <template #cell(createdDate)="row">
        {{ (dateFormat(row.item.creationDate, 'MMM DD hh:mm:ss A')) }}
      </template>

      <template #cell(acceptedDate)="row">
        {{ (row.item.acceptedDate ? dateFormat(row.item.acceptedDate, 'MMM DD hh:mm:ss A') : 'N/A') }}
      </template>

      <template #cell(permissions)="row">
        <span
          v-if="permissionLabels(row.item).length"
          :data-cy="'account-user-permissions-' + (row.item.id)"
        >
          <b-badge
            v-for="permission in permissionLabels(row.item)"
            :key="permission"
            class="mr-1"
            variant="light"
          >
            {{ permission }}
          </b-badge>
        </span>
        <span
          v-else
          :data-cy="'account-user-permissions-' + (row.item.id)"
        >
          No account access
        </span>
      </template>

      <template #cell(actions)="row">
        <div class="text-nowrap">
          <b-button
            v-if="canManageUsers"
            variant="secondary"
            size="sm"
            class="mr-2"
            :disabled="isLoading || !canEditPermissionsOf(row.item)"
            data-cy="account-user-edit-permissions"
            @click="showEditPermissions(row.item)"
          >
            Edit permissions
          </b-button>

          <b-button
            v-if="canManageUsers"
            variant="info"
            size="sm"
            :disabled="isLoading || isSelfUser(row.item)"
            data-cy="account-user-remove"
            @click="showConfirmRemoveUser(row.item)"
          >
            Remove
          </b-button>
        </div>
      </template>

      <template #cell(status)="row">
        <b-badge
          tag="div"
          class="w-100 py-1"
          :variant="getBadgeVariant(row.item.status)"
        >
          {{ startCase(row.item.status) }}
        </b-badge>
      </template>
    </b-table>

    <b-modal
      id="confirm-remove-user"
      ref="confirm-remove-user"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Remove"
      button-size="sm"
    >
      <p class="mb-2">
        Are you sure you want to remove user <strong>{{ selectedUser.email }}</strong>?
      </p>

      <p class="mb-0">
        <span class="text-muted">The user <strong>{{ selectedUser.email }}</strong> will no longer have access to your account, including data and settings associated with this account.</span>
      </p>

      <template #modal-footer>        
        <ButtonLoader
          type="button"
          variant="danger"
          size="sm"
          :loading="isLoading"
          @click="onRemoveUser"
        >
          Remove
        </ButtonLoader>
        
        <b-button
          type="button"
          variant="secondary"
          size="sm"          
          @click="$bvModal.hide('confirm-remove-user')"
        >
          Cancel
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="edit-account-permissions"
      ref="edit-account-permissions"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Edit account permissions"
      button-size="sm"
    >
      <p
        v-if="!showingSaveConfirmation"
        class="mb-2"
      >
        Choose the account permissions for <strong>{{ selectedUser.email }}</strong>.
      </p>

      <div v-if="!showingSaveConfirmation">
        <div
          v-for="permission in permissionOptions"
          :key="permission.value"
          class="mb-3"
        >
          <b-form-checkbox
            :id="'edit-account-user-permission-' + (permission.value)"
            v-model="selectedPermissions"
            :value="permission.value"
            :disabled="permission.value === 'billing' && selectedPermissions.includes('admin')"
            :data-cy="'edit-account-user-permission-' + (permission.value)"
          >
            {{ permission.text }}
          </b-form-checkbox>
          <AccountPermissionExplainer :value="permission.value" />
        </div>
        <p v-if="!selectedPermissions.length" role="alert" class="text-danger">
          Select at least one account permission.
        </p>
      </div>

      <div
        v-else
        data-cy="confirm-account-permissions"
      >
        <p>
          Are you sure you want to change account permissions for <strong>{{ selectedUser.email }}</strong>?
        </p>
        <AccountPermissionPreview
          :email="selectedUser.email"
          :before="currentPermissions"
          :after="selectedPermissions"
          :options="permissionOptions"
        />
      </div>

      <template #modal-footer>
        <ButtonLoader
          v-if="!showingSaveConfirmation"
          type="button"
          variant="primary"
          size="sm"
          :disabled="!hasPermissionChanges || !selectedPermissions.length"
          @click="showSaveConfirmation"
        >
          Review changes
        </ButtonLoader>

        <ButtonLoader
          v-else
          type="button"
          variant="primary"
          size="sm"
          :loading="isLoading"
          @click="onChangePermissions"
        >
          Save permissions
        </ButtonLoader>

        <b-button
          type="button"
          variant="secondary"
          size="sm"
          @click="cancelPermissionConfirmation"
        >
          Cancel
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '@/notification-utils'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import AccountPermissionExplainer from '@/components/account/AccountPermissionExplainer.vue'
import AccountPermissionPreview from '@/components/account/AccountPermissionPreview.vue'
import { accountPermissionOptions, effectiveMemberPermissions, canonicalPermissions } from '@/store/account-permissions'

export default {
  name: 'AccountUsers',
  components: {
    AccountPermissionExplainer,
    AccountPermissionPreview
  },
  mixins: [dateFormatMixin],

  props: {
    items: {
      type: Array,
      default: () => {}
    }
  },

  data() {
    return {
      isLoading: false,
      selectedUser: {},
      selectedPermissions: [],
      showingSaveConfirmation: false,
      fields: [
        { key: 'createdDate', label: 'Date Added', thClass: 'fixed-width-130' },
        { key: 'email', label: 'Email' },
        { key: 'firstName', label: 'FirstName' },
        { key: 'lastName', label: 'LastName' },       
        { key: 'permissions', label: 'Permissions' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },

  computed: {
    ...mapGetters('auth', ['userId', 'hasPermission', 'isSellerAccount']),

    permissionOptions() {
      return accountPermissionOptions(this.isSellerAccount)
    },

    canManageUsers() {
      return this.hasPermission('admin')
    },

    visibleFields() {
      return this.canManageUsers
        ? this.fields
        : this.fields.filter(field => field.key !== 'actions')
    },

    removedPermissionOptions() {
      const current = this.currentPermissions
      return this.permissionOptions.filter(option =>
        current.some(permission => permission.toLowerCase() === option.value) &&
        !this.selectedPermissions.includes(option.value)
      )
    },

    addedPermissionOptions() {
      const current = this.currentPermissions
      return this.permissionOptions.filter(option =>
        this.selectedPermissions.includes(option.value) &&
        !current.some(permission => permission.toLowerCase() === option.value)
      )
    },

    // Member rows are effective DTOs, not stored auth memberships.
    currentPermissions() {
      return effectiveMemberPermissions(this.selectedUser)
    },

    hasPermissionChanges() {
      return this.removedPermissionOptions.length > 0 || this.addedPermissionOptions.length > 0
    }
  },

  watch: {
    selectedPermissions(selection) {
      if (selection.includes('admin') && !selection.includes('billing')) {
        this.selectedPermissions = canonicalPermissions(selection)
      }
    }
  },

  methods: {

    ...mapActions('account', [
      'removeAccountUser',
      'setAccountUserPermissions'
    ]),

    // An admin changes another user, never their own permissions here.
    canEditPermissionsOf(user) {
      return this.canManageUsers && !this.isSelfUser(user)
    },

    rowClass(item) {
      return this.isSelfUser(item) ? 'text-nowrap current-user-row' : 'text-nowrap'
    },

    showEditPermissions(user) {
      if (this.isLoading || !this.canEditPermissionsOf(user)) {
        return
      }

      this.selectedUser = user
      this.showingSaveConfirmation = false
      this.selectedPermissions = canonicalPermissions(effectiveMemberPermissions(user))

      this.$bvModal.show('edit-account-permissions')
    },

    showSaveConfirmation() {
      if (this.isLoading || !this.canEditPermissionsOf(this.selectedUser) || !this.hasPermissionChanges || !this.selectedPermissions.length) {
        return
      }
      this.showingSaveConfirmation = true
    },

    cancelPermissionConfirmation() {
      if (this.showingSaveConfirmation) {
        this.showingSaveConfirmation = false
        return
      }

      this.$bvModal.hide('edit-account-permissions')
    },

    // One request at a time, for permission changes and Remove alike: the row buttons are disabled
    // while one is running, and what the request is about is read BEFORE awaiting, so the message
    // that follows names the member it was about whatever the selection has become since.
    async onChangePermissions() {
      if (this.isLoading || !this.canEditPermissionsOf(this.selectedUser) || !this.showingSaveConfirmation || !this.selectedPermissions.length) {
        return
      }

      const { id: userId, email } = this.selectedUser
      const permissions = canonicalPermissions(this.selectedPermissions)

      this.isLoading = true
      this.$bvModal.hide('edit-account-permissions')

      try {
        const result = await this.setAccountUserPermissions({ userId, permissions })

        showSuccessMessage(`Successfully updated permissions for ${email}`)

        // The server took the change; only the page could not be refreshed to show it.
        if (result?.refreshFailed) {
          showWarningMessage('Permissions were changed, but this page could not refresh. Reload the page to see the latest.')
        }
      } catch (error) {
        // 400 carries the reason in its message (for example removing the last full-permission member); 403
        // means the signed-in user is not an admin any more.
        const data = error.response?.data

        showErrorMessage(data?.title ?? data?.message ?? (typeof data === 'string' && data ? data : undefined))
      } finally {
        this.isLoading = false
        this.selectedUser = {}
        this.selectedPermissions = []
        this.showingSaveConfirmation = false
      }
    },

    permissionLabels(user) {
      const grants = effectiveMemberPermissions(user)
      return this.permissionOptions
        .filter(option => grants.some(granted => granted.toLowerCase() === option.value))
        .map(option => option.text)
    },

    isSelfUser(user) {
      return typeof user?.id === 'string' &&
        typeof this.userId === 'string' &&
        user.id.toLowerCase() === this.userId.toLowerCase()
    },

    getBadgeVariant(status) {
      const statusToVariantMap = {
        Pending: 'secondary',
        Accepted: 'success',
        Deleted: 'danger'
      }
      return statusToVariantMap[status]
    },

    async showConfirmRemoveUser(user){
      if (this.isLoading || !this.canManageUsers || this.isSelfUser(user)) {
        return
      }

      this.selectedUser = user
      this.$bvModal.show('confirm-remove-user')
    },

    async onRemoveUser() {
      if (this.isLoading || !this.canManageUsers || this.isSelfUser(this.selectedUser)) {
        return
      }

      const { id: userId, email } = this.selectedUser

      this.isLoading = true
      this.$bvModal.hide('confirm-remove-user')

      try {
        await this.removeAccountUser(userId)
        showSuccessMessage(`Successfully removed the user ${email} from this account`)
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
        this.selectedUser = {}
      }
    },

  }
}
</script>

<style lang="scss" scoped>
.current-user-row td {
  background: rgba(47, 128, 237, 0.12);
}
</style>
